import React, { useImperativeHandle, useRef, useState } from 'react';
import AccessTimeOutlined from '@mui/icons-material/AccessTimeOutlined';
import { Box, Button, IconButton, InputAdornment, Popover, Stack, MenuItem, TextField, Typography } from '@mui/material';
import { adminActionButtonSx } from 'src/theme/adminActionButton';

interface QuoteTimeInputProps {
  label: string;
  name: string;
  value: string;
  onChange: React.ChangeEventHandler<HTMLInputElement>;
  onCommitNext?: () => void;
  touchPicker?: boolean;
  /** Optional single formatted text field for attendance display; values still commit as HH:mm. */
  formatDisplay?: (clock: string) => string;
  parseDisplay?: (draft: string) => string | null;
  /** Client review: open the picker flush under its table cell, styled as an extension of the cell. */
  anchorToCell?: boolean;
  /** Select a bounded clock value with the existing widget instead of free text. */
  pickerOnly?: boolean;
}

export interface QuoteTimeInputHandle {
  focusAndOpen: () => void;
}

const QuoteTimeInput = React.forwardRef<QuoteTimeInputHandle, QuoteTimeInputProps>(({
  label,
  name,
  value,
  onChange,
  onCommitNext,
  touchPicker = false,
  formatDisplay,
  parseDisplay,
  anchorToCell = false,
  pickerOnly = false
}, ref) => {
  const input = useRef<HTMLInputElement>(null);
  const forwardTab = useRef(false);
  React.useEffect(() => {
    const key = (event: KeyboardEvent) => { forwardTab.current = event.key === 'Tab' && !event.shiftKey; };
    const pointer = () => { forwardTab.current = false; };
    document.addEventListener('keydown', key, true); document.addEventListener('pointerdown', pointer, true);
    return () => { document.removeEventListener('keydown', key, true); document.removeEventListener('pointerdown', pointer, true); };
  }, []);
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [hour, setHour] = useState('12');
  const [minute, setMinute] = useState('00');
  const [period, setPeriod] = useState('AM');
  const [manualDraft, setManualDraft] = useState<string | null>(null);
  const formattedMode = Boolean(formatDisplay && parseDisplay);
  const openClock = () => {
    // WebKit exposes showPicker for time inputs but can return without a UI.
    // Use the same chooser on every browser, retaining native typed HH:mm input.
    const [hours = '00', minutes = '00'] = value.split(':');
    const h = Number(hours) || 0;
    setHour(String(h % 12 || 12));
    setMinute(minutes || '00');
    setPeriod(h >= 12 ? 'PM' : 'AM');
    setAnchor(anchorToCell ? (input.current?.closest('td') as HTMLElement | null) ?? input.current : input.current);
  };
  const applyClock = () => {
    const hours = Number(hour) % 12 + (period === 'PM' ? 12 : 0);
    const nextValue = `${String(hours).padStart(2, '0')}:${minute}`;
    onChange({ target: { name, value: nextValue } } as React.ChangeEvent<HTMLInputElement>);
    setManualDraft(null);
    setAnchor(null);
    input.current?.focus();
  };

  useImperativeHandle(ref, () => ({
    focusAndOpen: () => {
      input.current?.focus();
      openClock();
    }
  }));

  return (
    <>
    <TextField
      size="small"
      type={formattedMode ? 'text' : 'time'}
      inputRef={input}
      inputProps={{
        'aria-label': label,
        readOnly: pickerOnly,
        'aria-keyshortcuts': 'Alt+ArrowDown'
      }}
      onClick={pickerOnly || !formattedMode ? openClock : undefined}
      name={name}
      value={manualDraft ?? (formattedMode ? formatDisplay!(value) : value)}
      onFocus={() => { if (name === 'startTime' && forwardTab.current) { forwardTab.current = false; openClock(); } }}
      onChange={(event) => {
        if (pickerOnly) return;
        if (!formattedMode) {
          onChange(event as React.ChangeEvent<HTMLInputElement>);
          return;
        }
        setManualDraft(event.target.value);
        const clock = parseDisplay!(event.target.value);
        if (clock !== null) {
          onChange({ target: { name, value: clock } } as React.ChangeEvent<HTMLInputElement>);
        }
      }}
      onBlur={() => { if (formattedMode) setManualDraft(null); }}
      onKeyDown={(event) => {
        if (pickerOnly && ['Enter', ' ', 'ArrowDown'].includes(event.key)) {
          event.preventDefault(); event.stopPropagation(); openClock(); return;
        }
        if (formattedMode && event.key === 'Enter' && manualDraft !== null &&
            parseDisplay!(manualDraft) === null) {
          event.preventDefault();
          event.stopPropagation();
          setManualDraft(null);
          return;
        }
        if ((event.key === 'Enter' || event.key === 'Tab') && !event.nativeEvent.isComposing && !event.shiftKey && onCommitNext) {
          event.preventDefault();
          event.stopPropagation();
          onCommitNext?.();
          return;
        }
        if (event.altKey && event.key === 'ArrowDown') {
          event.preventDefault();
          event.stopPropagation();
          openClock();
        }
      }}
      sx={{
        width: formattedMode ? 124 : 96,
        flexShrink: 0,
        '& .MuiInputBase-root': { padding: 0 },
        '& input::-webkit-datetime-edit': { padding: 0, textAlign: 'center' },
        '& input': {
          padding: '2px 0',
          fontSize: '12px',
          textAlign: 'center',
          minWidth: 0
        },
        '& input::-webkit-calendar-picker-indicator': { display: 'none' },
        '& .MuiOutlinedInput-notchedOutline': { border: 'none' }
      }}
      InputProps={{
        sx: { fontSize: '14px' },
        endAdornment: (
          <InputAdornment position="end" sx={{ ml: 0, mr: 0 }}>
            <IconButton
              size="small"
              tabIndex={-1}
              aria-label={`Open ${label.toLowerCase()} picker`}
              title={`Open ${label.toLowerCase()} picker (Alt+Down)`}
              onClick={event => { event.stopPropagation(); openClock(); }}
              sx={{ p: 0, ...(touchPicker ? { minHeight: 44, minWidth: 36 } : {}) }}
            >
              <AccessTimeOutlined sx={{ fontSize: 15 }} />
            </IconButton>
          </InputAdornment>
        )
      }}
      InputLabelProps={{ shrink: true }}
    />
    <Popover open={Boolean(anchor)} anchorEl={anchor}
      onClose={() => setAnchor(null)}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      PaperProps={{ role: 'dialog', 'aria-label': `${label} picker`, 'data-anchored-to-cell': anchorToCell ? 'true' : undefined, sx: { p: 2, minWidth: 260, ...(anchorToCell ? { mt: '-1px', border: '1px solid #b9ae8f', borderTop: '2px solid #e8bf21', borderRadius: '0 0 6px 6px', boxShadow: '0 6px 16px rgba(0,0,0,.12)' } : {}) } }}>
      <Box onKeyDown={(event) => {
        // Portal events still bubble to the row's Enter/Escape save/cancel handlers.
        event.stopPropagation();
        if (event.key === 'Escape') { event.preventDefault(); setAnchor(null); }
      }}>
        <Typography component="h2" variant="subtitle2" sx={{ mb: 1 }}>{label}</Typography>
        <Stack direction="row" spacing={1}>
          <TextField select SelectProps={{ native: !touchPicker }}
            label="Hour" size="small"
            value={hour}
            inputProps={{ 'aria-label': `${label} hour` }} onChange={event => setHour(event.target.value)}>
            {Array.from({ length: 12 }, (_, i) => i + 1).map(h => touchPicker ? <MenuItem key={h} value={h}>{h}</MenuItem> : <option key={h} value={h}>{h}</option>)}
          </TextField>
          <TextField select SelectProps={{ native: !touchPicker }}
            label="Minute" size="small"
            value={minute}
            inputProps={{ 'aria-label': `${label} minute` }} onChange={event => setMinute(event.target.value)}>
            {Array.from({ length: 60 }, (_, i) => String(i).padStart(2, '0')).map(m => touchPicker ? <MenuItem key={m} value={m}>{m}</MenuItem> : <option key={m} value={m}>{m}</option>)}
          </TextField>
          <TextField select SelectProps={{ native: !touchPicker }}
            label="AM/PM" size="small"
            value={period}
            inputProps={{ 'aria-label': `${label} period` }} onChange={event => setPeriod(event.target.value)}>
            {touchPicker ? [<MenuItem key="AM" value="AM">AM</MenuItem>, <MenuItem key="PM" value="PM">PM</MenuItem>] : [<option key="AM" value="AM">AM</option>, <option key="PM" value="PM">PM</option>]}
          </TextField>
        </Stack>
        <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 2 }}>
          <Button size={pickerOnly ? 'small' : undefined} variant={pickerOnly ? 'outlined' : 'text'} color={pickerOnly ? 'inherit' : 'primary'} onClick={() => setAnchor(null)}>Cancel</Button>
          <Button size={pickerOnly ? 'small' : undefined} variant="contained" sx={pickerOnly ? adminActionButtonSx : undefined} onClick={applyClock}>Apply Time</Button>
        </Stack>
      </Box>
    </Popover>
    </>
  );
});

QuoteTimeInput.displayName = 'QuoteTimeInput';

export default QuoteTimeInput;
